import type { FC, ReactNode } from 'react'
import type { ArticleSummary } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'

interface ArticleCardProps {
    article: ArticleSummary
    variant?: 'feature' | 'row' | 'card'
    className?: string
}

const Meta: FC<{ article: ArticleSummary }> = ({ article }): ReactNode => {
    const parts = [
        article.publishedAt ? formatDate(article.publishedAt) : '',
        article.readingTime ? `${article.readingTime} min read` : ''
    ].filter(Boolean)

    return (
        <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-charcoal/80'>
            {article.publishedAt && <time dateTime={article.publishedAt}>{parts[0]}</time>}
            {parts.length > 1 && <span aria-hidden='true'> / </span>}
            {article.readingTime ? <span>{article.readingTime} min read</span> : null}
        </p>
    )
}

const ArticleCard: FC<ArticleCardProps> = ({ article, variant = 'card', className }): ReactNode => {
    const href = `/insights/${article.slug}`

    if (variant === 'row') {
        return (
            <article data-reveal className={cn('group', className)}>
                <Link href={href} className='grid grid-cols-[7rem_1fr] gap-5 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-charcoal sm:grid-cols-[10rem_1fr] sm:gap-6'>
                    <div className='relative aspect-square overflow-hidden bg-stone'>
                        {article.cover && (
                            <Image src={article.cover} alt={article.coverAlt} fill sizes='160px' className='object-cover transition-[scale] duration-700 ease-[var(--ease-out-expo)] group-hover:scale-[1.06]' />
                        )}
                    </div>
                    <div className='flex flex-col'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal'>{article.category}</p>
                        <h3 className='mt-2 font-heading text-lg font-extrabold leading-tight tracking-[-0.02em] underline-offset-4 group-hover:underline sm:text-xl'>
                            {article.title}
                        </h3>
                        <div className='mt-auto pt-3'>
                            <Meta article={article} />
                        </div>
                    </div>
                </Link>
            </article>
        )
    }

    const feature = variant === 'feature'

    return (
        <article data-reveal className={cn('group', className)}>
            <Link href={href} className='block rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-charcoal'>
                <div data-reveal-image className={cn('relative overflow-hidden bg-stone', feature ? 'aspect-[4/3]' : 'aspect-[3/2]')}>
                    {article.cover && (
                        <Image
                            src={article.cover}
                            alt={article.coverAlt}
                            fill
                            sizes={feature ? '(min-width: 768px) 50vw, 100vw' : '(min-width: 768px) 33vw, 100vw'}
                            className='object-cover transition-[scale] duration-[1400ms] ease-[var(--ease-out-expo)] group-hover:scale-[1.04]'
                        />
                    )}
                    <span className='absolute left-3 top-3 rounded-sm bg-cream px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-charcoal'>
                        {article.category}
                    </span>
                    <span className='absolute bottom-3 right-3 flex size-11 items-center justify-center rounded-sm bg-coral text-charcoal transition-transform duration-300 group-hover:-translate-y-1'>
                        <ArrowUpRight aria-hidden='true' className='size-5' />
                    </span>
                </div>

                <div className='mt-5'>
                    <Meta article={article} />
                    <h3
                        className={cn(
                            'mt-3 font-heading font-extrabold leading-[1.08] tracking-[-0.025em] underline-offset-[6px] group-hover:underline',
                            feature ? 'text-[clamp(1.6rem,2.6vw,2.35rem)]' : 'text-xl md:text-[1.6rem]'
                        )}
                    >
                        {article.title}
                    </h3>
                    <p className='mt-3 max-w-lg text-[15px] leading-relaxed text-charcoal/80'>
                        {article.excerpt}
                    </p>
                </div>
            </Link>
        </article>
    )
}

export default ArticleCard
